import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AlertCircle, Eye, EyeOff, Building2, User, Mail, Phone } from 'lucide-react';

export const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    companyName: '',
    cnpj: '',
    email: '',
    phone: '',
    adminName: '',
    adminEmail: '',
    password: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateCNPJ = (cnpj: string): boolean => {
    const cleaned = cnpj.replace(/[^\d]/g, '');
    return cleaned.length === 14;
  };

  const formatCNPJ = (value: string): string => {
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length <= 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
    }
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const formatPhone = (value: string): string => {
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length <= 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    }
    return cleaned.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (!validateCNPJ(formData.cnpj)) {
      setError('CNPJ inválido');
      return;
    }

    setIsLoading(true);

    try {
      const result = await register({
        companyName: formData.companyName,
        cnpj: formData.cnpj.replace(/[^\d]/g, ''),
        email: formData.email,
        phone: formData.phone.replace(/[^\d]/g, ''),
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        password: formData.password
      });

      if (result.success) {
        navigate('/onboarding/plano');
      } else {
        setError(result.error || 'Erro ao criar conta');
      }
    } catch (err) {
      setError('Erro ao criar conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Criar Conta | Portal Ethics</title>
      </Helmet>
      
      <div className="min-h-screen bg-background-light py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <span className="text-3xl font-bold text-primary-blue">Portal Ethics</span>
            </Link>
            <h2 className="mt-6 text-3xl font-extrabold text-neutral-900">
              Crie sua conta
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              Comece a transformar a cultura ética da sua empresa
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary-blue" />
                  Dados da Empresa
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Nome da Empresa"
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Ex: Minha Empresa LTDA"
                    required
                    icon={<Building2 className="w-4 h-4" />}
                  />

                  <Input
                    label="CNPJ"
                    name="cnpj"
                    type="text"
                    value={formData.cnpj}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const formatted = formatCNPJ(e.target.value);
                      setFormData(prev => ({ ...prev, cnpj: formatted }));
                    }}
                    placeholder="00.000.000/0000-00"
                    required
                    maxLength={18}
                  />

                  <Input
                    label="Email da Empresa"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contato@empresa.com"
                    required
                    icon={<Mail className="w-4 h-4" />}
                  />

                  <Input
                    label="Telefone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const formatted = formatPhone(e.target.value);
                      setFormData(prev => ({ ...prev, phone: formatted }));
                    }}
                    placeholder="(00) 00000-0000"
                    required
                    icon={<Phone className="w-4 h-4" />}
                  />
                </div>
              </div>

              <div className="border-t border-neutral-200"></div>

              {/* Admin Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary-blue" />
                  Dados do Administrador
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Nome Completo"
                    name="adminName"
                    type="text"
                    value={formData.adminName}
                    onChange={handleChange}
                    placeholder="Seu nome completo"
                    required
                    icon={<User className="w-4 h-4" />}
                  />

                  <Input
                    label="Email"
                    name="adminEmail"
                    type="email"
                    value={formData.adminEmail}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    required
                    icon={<Mail className="w-4 h-4" />}
                  />

                  <div className="relative">
                    <Input
                      label="Senha"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-[34px] text-neutral-400 hover:text-neutral-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  <div className="relative">
                    <Input
                      label="Confirmar Senha"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Repita a senha"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-[34px] text-neutral-400 hover:text-neutral-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Criando conta...' : 'Continuar'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-600">
                Já tem uma conta?{' '}
                <Link to="/login" className="font-medium text-primary-blue hover:text-primary-darkBlue">
                  Faça login
                </Link>
              </p>
            </div>
          </div>

          <p className="mt-4 text-center text-xs text-neutral-500">
            Ao criar uma conta, você concorda com nossos{' '}
            <Link to="/termos" className="text-primary-blue hover:underline">
              Termos de Serviço
            </Link>{' '}
            e{' '}
            <Link to="/privacidade" className="text-primary-blue hover:underline">
              Política de Privacidade
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};
